//
//  useFitinsightUserList.ts
//
//  isana.net,inc. Proprietary and Confidential
//
import { useCallback, useEffect, useMemo, useState } from "react";
import { getFitinsightUserList } from "../network/api";
import { FitinsightUser } from "../network/type";
import useProgress from "./useProgress";
import useFilters from "./useFilters";
import { FitinsightUserTableRow } from "../component/FitinsightUserTable";
import { checkDateStringFormat } from "../utils/time";
import dayjs from "dayjs";

const ROWS_PER_PAGE = 30;

const useFitinsightUserList = () => {
  const progressDialog = useProgress();
  const [originalUserList, setOriginalUserList] = useState<FitinsightUser[]>(
    []
  );
  const [page, setPage] = useState<number>(0);

  const {
    filterList,
    activeFilters,
    filteredItems: filteredUserList,
    checkedAll,
    handleChecked,
  } = useFilters(originalUserList, "companyId");

  useEffect(() => {
    progressDialog.show(async () => {
      await init();
    });
  }, []);

  useEffect(() => {
    // フィルタリングされたユーザリストが変更されたときにページをリセット
    setPage(0);
  }, [filteredUserList]);

  const init = useCallback(async () => {
    const users = await fetchFitinsightUserList();

    if (users.length === 0) return;

    setOriginalUserList(users);
  }, []);

  const fetchFitinsightUserList = useCallback(async (): Promise<
    FitinsightUser[]
  > => {
    let count = 1;
    let continueFetching = true;
    let users: FitinsightUser[] = [];
    while (continueFetching) {
      const response = await getFitinsightUserList(count);
      if (response.status === 200 && response.data) {
        if (response.data.users.length === 0) {
          continueFetching = false;
          break;
        }
        users = [...users, ...response.data.users];
        continueFetching = response.data.isNext;
      } else {
        // TODO: エラーハンドリング
        console.error("Error fetching user list:", response);
        users = [];
        continueFetching = false;
      }
      count++;
    }
    return users;
  }, []);

  const shouldShowAlert = useCallback((latestFetchDate: string | undefined) => {
    if (!latestFetchDate || !checkDateStringFormat(latestFetchDate)) {
      // 最新データ取得日がない=未取得の場合 もしくは 日付フォーマットが正しくない場合
      return true;
    }

    // 最新データ取得日から3日以上経過している場合
    const threeDaysAgo = dayjs().subtract(3, "day");
    return dayjs(latestFetchDate).isBefore(threeDaysAgo);
  }, []);

  const displayedUsers = useMemo((): FitinsightUserTableRow[] => {
    const start = page * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    const slicedUserList = filteredUserList.slice(start, end);
    return slicedUserList.map((user) => ({
      companyId: user.companyId,
      fitinsightUniqueId: user.fitinsightUniqueId,
      latestFetchDate: user.latestFetchDate,
      // [FITBIT-MIGRATION] GHA未移行の目印表示用。Fitbit廃止時に削除可（E3-4）。
      integrationType: user.integrationType,
      rowType: shouldShowAlert(user.latestFetchDate) ? "alert" : "normal",
    }));
  }, [page, filteredUserList]);

  const handlePageChange = useCallback((page: number) => {
    setPage(page);
  }, []);

  return {
    filterList,
    activeFilters,
    originalUserCount: originalUserList.length,
    filteredUserCount: filteredUserList.length,
    displayedUsers,
    showProgress: progressDialog.open,
    rowsPerPage: ROWS_PER_PAGE,
    page,
    checkedAll,
    handleChecked,
    handlePageChange,
  };
};

export default useFitinsightUserList;
